import { useState, useEffect, useRef, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { InlineMath, BlockMath } from 'react-katex';
import 'katex/dist/katex.min.css';
import Plot from 'react-plotly.js';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import { Edit2, Copy, Download, ZoomIn, BarChart, LineChart, PieChart, Check, X, Type, Eye } from 'lucide-react';
import { Button } from '../ui/Button';

interface MessageContentProps {
  content: string;
  onEdit?: (content: string) => void;
  isStreaming?: boolean;
}

interface ChartData {
  type: 'bar' | 'line' | 'pie';
  data: any;
}

export function MessageContent({ content, onEdit, isStreaming = false }: MessageContentProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [selectedChartType, setSelectedChartType] = useState<'bar' | 'line' | 'pie'>('bar');
  const [isZoomed, setIsZoomed] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // S'assurer que content est toujours une string valide
  // Gérer le cas où content est un array (données corrompues dans localStorage)
  const safeContent = (() => {
    if (typeof content === 'string') return content;
    if (Array.isArray(content)) {
      // Extraire uniquement les strings de l'array, ignorer les booleans/autres types
      return content.filter((item): item is string => typeof item === 'string').join('');
    }
    if (content === null || content === undefined) return '';
    return String(content);
  })();
  
  const [editedContent, setEditedContent] = useState(safeContent);

  // Effet pour mettre à jour le contenu édité quand le contenu change (streaming)
  useEffect(() => {
    setEditedContent(safeContent);
  }, [safeContent]);

  // Auto-resize textarea comme Notion
  const adjustTextareaHeight = useCallback(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${Math.max(textarea.scrollHeight, 120)}px`;
    }
  }, []);

  // Ajuster la hauteur quand on commence à éditer ou quand le contenu change
  useEffect(() => {
    if (isEditing) {
      adjustTextareaHeight();
    }
  }, [isEditing, editedContent, adjustTextareaHeight]);

  // Focus sur le textarea quand on commence à éditer
  useEffect(() => {
    if (isEditing && textareaRef.current) {
      textareaRef.current.focus();
      // Placer le curseur à la fin
      textareaRef.current.setSelectionRange(
        textareaRef.current.value.length,
        textareaRef.current.value.length
      );
    }
  }, [isEditing]);

  const handleCopy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Échec de la copie:', error);
    }
  };

  const parseChartData = (code: string): ChartData | null => {
    try {
      const data = JSON.parse(code);
      return {
        type: 'bar',
        data
      };
    } catch {
      return null;
    }
  };

  const handleCopyCode = (code: string) => {
    handleCopy(code);
  };

  const handleDownloadChart = (chartData: any) => {
    const blob = new Blob([JSON.stringify(chartData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'chart-data.json';
    document.body.appendChild(a);
    a.click();
    try {
      document.body.removeChild(a);
    } catch (e) {
      // Ignore if already removed
    }
    URL.revokeObjectURL(url);
  };

  const handleSaveEdit = () => {
    onEdit?.(editedContent);
    setIsEditing(false);
    setShowPreview(false);
  };

  const handleCancelEdit = () => {
    setEditedContent(safeContent);
    setIsEditing(false);
    setShowPreview(false);
  };

  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setEditedContent(e.target.value);
    adjustTextareaHeight();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Cmd/Ctrl + Enter pour sauvegarder
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault();
      handleSaveEdit();
    }
    // Escape pour annuler
    if (e.key === 'Escape') {
      e.preventDefault();
      handleCancelEdit();
    }
  };

  const renderContent = () => {
    if (isEditing) {
      return (
        <div className="space-y-3">
          {/* Toolbar style Notion */}
          <div className="flex items-center justify-between pb-2 border-b border-border">
            <div className="flex items-center space-x-1">
              <button
                onClick={() => setShowPreview(false)}
                className={`
                  px-3 py-1.5 text-xs font-medium rounded-md transition-colors
                  ${!showPreview 
                    ? 'bg-primary/10 text-primary' 
                    : 'text-muted-foreground hover:bg-muted'
                  }
                `}
              >
                <Type className="h-3 w-3 inline mr-1.5" />
                Éditer
              </button>
              <button
                onClick={() => setShowPreview(true)}
                className={`
                  px-3 py-1.5 text-xs font-medium rounded-md transition-colors
                  ${showPreview 
                    ? 'bg-primary/10 text-primary' 
                    : 'text-muted-foreground hover:bg-muted'
                  }
                `}
              >
                <Eye className="h-3 w-3 inline mr-1.5" />
                Aperçu
              </button>
            </div>
            <span className="text-xs text-muted-foreground">
              ⌘+Enter pour sauvegarder • Esc pour annuler
            </span>
          </div>

          {/* Zone d'édition ou aperçu */}
          {showPreview ? (
            <div className="min-h-[120px] p-4 bg-muted/30 rounded-lg border border-border">
              <ReactMarkdown
                remarkPlugins={[remarkGfm, remarkMath]}
                rehypePlugins={[rehypeKatex]}
                className="prose prose-sm dark:prose-invert max-w-none"
              >
                {editedContent}
              </ReactMarkdown>
            </div>
          ) : (
            <textarea
              ref={textareaRef}
              value={editedContent}
              onChange={handleTextareaChange}
              onKeyDown={handleKeyDown}
              placeholder="Écrivez votre message... (Markdown supporté)"
              className="
                w-full min-h-[120px] p-4 
                bg-muted/30 border border-border rounded-lg
                text-sm font-mono leading-relaxed
                resize-none overflow-hidden
                focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary
                placeholder:text-muted-foreground/50
                transition-all
              "
              style={{ height: 'auto' }}
            />
          )}

          {/* Actions */}
          <div className="flex items-center justify-between pt-2">
            <span className="text-xs text-muted-foreground">
              {editedContent.length} caractères
            </span>
            <div className="flex items-center space-x-2">
              <button
                onClick={handleCancelEdit}
                className="px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={handleSaveEdit}
                className="px-4 py-1.5 text-sm bg-primary text-primary-foreground hover:bg-primary/90 rounded-lg transition-colors flex items-center space-x-1.5"
              >
                <Check className="h-3.5 w-3.5" />
                <span>Enregistrer</span>
              </button>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        <div className="relative">
          <ReactMarkdown
            key={isStreaming ? 'streaming' : 'static'}
            remarkPlugins={[remarkGfm, remarkMath]}
            rehypePlugins={[rehypeKatex]}
            components={{
              // Tables avec style amélioré
              table: ({ children }) => (
                <div className="overflow-x-auto my-4">
                  <table className="min-w-full border-collapse border border-gray-300 rounded-lg">
                    {children}
                  </table>
                </div>
              ),
              thead: ({ children }) => (
                <thead className="bg-gray-100">{children}</thead>
              ),
              tbody: ({ children }) => (
                <tbody className="divide-y divide-gray-200">{children}</tbody>
              ),
              tr: ({ children }) => (
                <tr className="hover:bg-gray-50">{children}</tr>
              ),
              th: ({ children }) => (
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border border-gray-300">
                  {children}
                </th>
              ),
              td: ({ children }) => (
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 border border-gray-300">
                  {children}
                </td>
              ),
              
              // Support des diagrammes Mermaid
              code({ node, className, children, ...props }) {
                const match = /language-(\w+)/.exec(className || '');
                const language = match ? match[1] : '';
                const isInline = !className?.includes('language-');
                
                if (isInline) {
                  return (
                    <code className="bg-muted px-1.5 py-0.5 rounded text-sm font-mono" {...props}>
                      {children}
                    </code>
                  );
                }

                if (language === 'chart') {
                  const chartData = parseChartData(String(children));
                  if (!chartData) return null;

                  return (
                    <div className="relative bg-card border rounded-lg p-4">
                      <div className="absolute top-2 right-2 flex space-x-1 z-10">
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => setIsZoomed(!isZoomed)}
                          className="h-8 w-8 p-0"
                        >
                          <ZoomIn className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => handleDownloadChart(chartData.data)}
                          className="h-8 w-8 p-0"
                        >
                          <Download className="h-4 w-4" />
                        </Button>
                        <div className="flex space-x-1">
                          <Button
                            variant={selectedChartType === 'bar' ? 'primary' : 'secondary'}
                            size="sm"
                            onClick={() => setSelectedChartType('bar')}
                            className="h-8 w-8 p-0"
                          >
                            <BarChart className="h-3 w-3" />
                          </Button>
                          <Button
                            variant={selectedChartType === 'line' ? 'primary' : 'secondary'}
                            size="sm"
                            onClick={() => setSelectedChartType('line')}
                            className="h-8 w-8 p-0"
                          >
                            <LineChart className="h-3 w-3" />
                          </Button>
                          <Button
                            variant={selectedChartType === 'pie' ? 'primary' : 'secondary'}
                            size="sm"
                            onClick={() => setSelectedChartType('pie')}
                            className="h-8 w-8 p-0"
                          >
                            <PieChart className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                      <div className={isZoomed ? 'fixed inset-4 z-50 bg-card p-6 shadow-xl rounded-lg border' : ''}>
                        {isZoomed && (
                          <button
                            onClick={() => setIsZoomed(false)}
                            className="absolute top-4 right-4 text-muted-foreground hover:text-foreground"
                          >
                            ✕
                          </button>
                        )}
                        <Plot
                          data={[{
                            ...chartData.data,
                            type: selectedChartType
                          }]}
                          layout={{
                            autosize: true,
                            margin: { t: 60, r: 10, l: 60, b: 40 },
                            paper_bgcolor: 'transparent',
                            plot_bgcolor: 'transparent'
                          }}
                          style={{ width: '100%', height: isZoomed ? '80vh' : '300px' }}
                          config={{ responsive: true }}
                        />
                      </div>
                    </div>
                  );
                }

                return (
                  <div className="relative group bg-muted border rounded-lg overflow-hidden">
                    <div className="flex items-center justify-between px-4 py-2 bg-muted/50 border-b">
                      <span className="text-xs font-medium text-muted-foreground uppercase">
                        {language || 'code'}
                      </span>
                      <button
                        className="flex items-center space-x-1 px-2 py-1 text-xs bg-secondary hover:bg-secondary/80 rounded transition-colors"
                        onClick={() => handleCopyCode(String(children))}
                      >
                        {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                        <span>{copied ? 'Copié' : 'Copier'}</span>
                      </button>
                    </div>
                    <SyntaxHighlighter
                      language={language}
                      style={vscDarkPlus}
                      customStyle={{ 
                        margin: 0, 
                        padding: '1rem',
                        background: 'transparent' 
                      }}
                    >
                      {String(children).replace(/\n$/, '')}
                    </SyntaxHighlighter>
                  </div>
                );
              },
              p({ children }) {
                // Gérer les expressions mathématiques inline et en bloc
                if (typeof children === 'string') {
                  // Support des expressions LaTeX en bloc $$...$$
                  if (children.includes('$$')) {
                    const parts = children.split(/($$.*?$$)/gs);
                    return (
                      <div className="leading-relaxed">
                        {parts.map((part, i) => {
                          if (part.startsWith('$$') && part.endsWith('$$')) {
                            return (
                              <div key={i} className="my-4">
                                <BlockMath>
                                  {part.slice(2, -2)}
                                </BlockMath>
                              </div>
                            );
                          }
                          return <span key={i}>{part}</span>;
                        })}
                      </div>
                    );
                  }
                  
                  // Support des expressions LaTeX inline $...$
                  if (children.includes('$')) {
                    const parts = children.split(/(\$[^$]+\$)/g);
                    return (
                      <p className="leading-relaxed">
                        {parts.map((part, i) => {
                          if (part.startsWith('$') && part.endsWith('$') && part.length > 2) {
                            return (
                              <InlineMath key={i}>
                                {part.slice(1, -1)}
                              </InlineMath>
                            );
                          }
                          return part;
                        })}
                      </p>
                    );
                  }
                }
                return <p className="leading-relaxed">{children}</p>;
              },
              h1: ({ children }) => <h1 className="text-2xl font-bold mb-4">{children}</h1>,
              h2: ({ children }) => <h2 className="text-xl font-semibold mb-3">{children}</h2>,
              h3: ({ children }) => <h3 className="text-lg font-medium mb-2">{children}</h3>,
              ul: ({ children }) => <ul className="list-disc list-inside space-y-1 ml-4">{children}</ul>,
              ol: ({ children }) => <ol className="list-decimal list-inside space-y-1 ml-4">{children}</ol>,
              blockquote: ({ children }) => (
                <blockquote className="border-l-4 border-primary pl-4 italic bg-muted/30 py-2 rounded-r">
                  {children}
                </blockquote>
              ),
              a: ({ href, children }) => (
                <a
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary underline hover:text-primary/80 transition-colors"
                >
                  {children}
                </a>
              ),
            }}
          >
            {safeContent}
          </ReactMarkdown>
          {/* Curseur de streaming style terminal - placé après ReactMarkdown */}
          {isStreaming && <span className="streaming-cursor" />}
          
          {/* Indicateur de streaming subtil */}
          {isStreaming && safeContent && (
            <div className="flex items-center space-x-2 mt-3 text-xs text-muted-foreground">
              <div className="flex space-x-0.5">
                <span className="w-1 h-3 bg-primary/60 rounded-full animate-wave" style={{ animationDelay: '0ms' }} />
                <span className="w-1 h-3 bg-primary/60 rounded-full animate-wave" style={{ animationDelay: '100ms' }} />
                <span className="w-1 h-3 bg-primary/60 rounded-full animate-wave" style={{ animationDelay: '200ms' }} />
              </div>
              <span className="animate-pulse-subtle">Génération en cours</span>
            </div>
          )}
        </div>

        {onEdit && !isStreaming && (
          <div className="group-hover:opacity-100 opacity-0 transition-opacity">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsEditing(true)}
              className="h-7 text-xs text-muted-foreground hover:text-foreground"
            >
              <Edit2 className="h-3 w-3 mr-1" />
              Modifier
            </Button>
          </div>
        )}
      </div>
    );
  };

  return renderContent();
}